
import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Card from '../../components/base/Card';
import { supabaseService } from '../../utils/supabase';

interface ExperienceItem {
  id: string;
  company: string;
  position: string;
  startDate: string;
  endDate: string;
  description: string;
  achievements: string[];
  image?: string;
}

export default function Experience() {
  const [experienceData, setExperienceData] = useState<ExperienceItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadExperienceData();
  }, []);

  const loadExperienceData = async () => {
    try {
      const data = await supabaseService.getAllData();
      if (data.experience && data.experience.length > 0) {
        setExperienceData(data.experience);
      }
    } catch (error) {
      console.error('加载经验数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-white to-slate-200">
      <Navbar />
      
      <div className="pt-20 pb-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Service</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Prepare yourself
            </p>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">加载中...</p>
            </div>
          )}

          {/* Empty State */}
          {!loading && (!experienceData || experienceData.length === 0) && (
            <div className="text-center py-12">
              <i className="ri-briefcase-line text-6xl text-gray-400 mb-4"></i>
              <h3 className="text-xl font-medium text-gray-600 mb-2">暂无服务经历</h3>
              <p className="text-gray-500">请通过管理后台添加服务经历</p>
            </div>
          )}

          {/* Experience Timeline */}
          {!loading && experienceData && experienceData.length > 0 && (
            <div className="space-y-8">
              {experienceData.map((exp, index) => (
              <Card key={exp.id} className="p-6 hover:shadow-lg transition-shadow" glass>
                <div className="flex flex-col lg:flex-row gap-6">
                  {/* Experience Info */}
                  <div className="flex-1">
                    <div className="flex items-start space-x-4 mb-4">
                      {/* Timeline dot */}
                      <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg">
                        <i className="ri-briefcase-line text-white text-lg"></i>
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-2">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">{exp.company}</h3>
                            <p className="text-blue-600 font-medium">{exp.position}</p>
                          </div>
                          <div className="text-right mt-5 md:mt-0">
                            <p className="text-gray-600 font-medium">{exp.startDate} - {exp.endDate}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="ml-16">
                      <p className="text-gray-700 leading-relaxed mb-4">{exp.description}</p>
                      
                      {/* Achievements */}
                      {exp.achievements && exp.achievements.length > 0 && (
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">主要成就：</h4>
                          <div className="grid md:grid-cols-2 gap-2">
                            {exp.achievements.map((achievement, achIndex) => (
                              <div key={achIndex} className="flex items-center space-x-2">
                                <i className="ri-check-line text-green-500 flex-shrink-0"></i>
                                <span className="text-gray-700 text-sm">{achievement}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Experience Image */}
                  {exp.image && (
                    <div className="lg:w-64 flex-shrink-0">
                      <img 
                        src={exp.image} 
                        alt={exp.company}
                        className="w-full h-48 lg:h-full object-cover object-top rounded-lg border shadow-sm"
                      />
                    </div>
                  )}
                </div>
              </Card>
            ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
